/**
 * Turning a mailbox sync result into the one line the operator reads.
 *
 * Kept out of the component because the interesting case is the failure, and a
 * failure that says only "Sync failed" is what made a switched-off inbound look
 * identical to a wrong IMAP password for as long as the button has existed. The
 * API answers with a stable reason code where it has one (`imap_not_configured`,
 * `already_polling`) and the raw connection error where it does not - so unknown
 * reasons are shown verbatim rather than swallowed into the generic string.
 */
import type { SyncResult } from "../../../lib/mail";

type T = (key: string, vars?: Record<string, string | number>) => string;

/** The longest raw IMAP error we put in a toast before it stops being readable. */
const RAW_REASON_MAX = 120;

export function syncMessage(t: T, r: SyncResult): string {
  if (r.ok) {
    // A first sync deliberately imports nothing: it only records where the
    // mailbox is now. "0 new messages" would read as a failure, so say what
    // actually happened.
    if (r.baseline) return t("email.syncBaseline");
    return t("email.syncOk").replace("{n}", String(r.ingested));
  }

  const reason = r.reason?.trim();
  if (!reason) return t("email.syncFailed");

  // `t` hands back the key itself when the catalogue has no entry, which is how
  // an unknown reason is told apart from a translated one.
  const key = `email.syncReason.${reason}`;
  const translated = t(key);
  if (translated !== key) return translated;

  const raw = reason.length > RAW_REASON_MAX ? `${reason.slice(0, RAW_REASON_MAX)}...` : reason;
  return `${t("email.syncFailed")} ${raw}`;
}
